import app from 'flarum/forum/app';
import Page from 'flarum/common/components/Page';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import Link from 'flarum/common/components/Link';
import Button from 'flarum/common/components/Button';
import EditUserModal from 'flarum/common/components/EditUserModal';
import UserCard from 'flarum/forum/components/UserCard';
import ProfileDetailsModal from './ProfileDetailsModal';
import pakt21ApiUrl from '../../common/pakt21ApiUrl';

function statusLabel(status) {
  return app.translator.trans(`pakt21-beta.forum.id_page.status_${status || 'unknown'}`);
}

export default class Pakt21IdPage extends Page {
  oninit(vnode) {
    super.oninit(vnode);

    this.bodyClass = 'App--pakt21Id';
    this.number = String(vnode.attrs.number || '');
    this.user = null;
    this.idRecord = null;
    this.error = null;

    app.request({
      method: 'GET',
      url: pakt21ApiUrl(app.forum.attribute('apiUrl'), `/pakt21/users/by-number/${encodeURIComponent(this.number)}`),
    })
      .then((payload) => {
        app.store.pushPayload(payload);
        this.user = app.store.getById('users', payload.data.id);
        this.idRecord = payload.meta?.pakt21Id || null;
        app.setTitle(`PAKT21 ID #${this.number}`);
        m.redraw();
      })
      .catch(() => {
        this.error = true;
        m.redraw();
      });
  }

  canEditUser() {
    const actor = app.session.user;

    return Boolean(actor && (actor === this.user || actor.isAdmin()));
  }

  editUser() {
    const actor = app.session.user;

    if (!actor || !this.user) return;

    app.modal.show(actor.isAdmin() ? EditUserModal : ProfileDetailsModal, { user: this.user });
  }

  view() {
    if (this.error) {
      return (
        <div className="container Pakt21IdPage">
          <div className="Pakt21IdPage-card">
            <h1>{app.translator.trans('pakt21-beta.forum.id_page.not_found_title', { number: this.number })}</h1>
            <p>{app.translator.trans('pakt21-beta.forum.id_page.not_found_text')}</p>
            <Link className="Button Button--primary" href="/">
              {app.translator.trans('pakt21-beta.forum.id_page.back')}
            </Link>
          </div>
        </div>
      );
    }

    if (!this.user) {
      return <LoadingIndicator display="block" />;
    }

    const currentNumber = this.user.attribute('pakt21MemberNumber');
    const isHistoricalNumber = this.idRecord ? !this.idRecord.isCurrent : String(currentNumber) !== this.number;
    const location = this.user.attribute('pakt21Location');
    const contribution = this.user.attribute('pakt21Contribution');
    const needs = this.user.attribute('pakt21Needs');
    const skills = this.user.attribute('pakt21Skills') || [];
    const joined = this.user.joinTime();

    return (
      <div className="Pakt21IdPage">
        <UserCard user={this.user} className="Hero UserHero" editable={false} pakt21Compact={true} controlsButtonClassName="Button" />
        <div className="container containerNarrow">
          <section className="Pakt21IdPage-card">
            <p className="Pakt21IdPage-label">{app.translator.trans('pakt21-beta.forum.id_page.card_label')}</p>
            <div className="Pakt21IdPage-titleRow">
              <h1>PAKT21 ID #{this.number}</h1>
              <span className={`Pakt21IdStatus Pakt21IdStatus--${this.idRecord?.status || 'unknown'}`}>
                {statusLabel(this.idRecord?.status)}
              </span>
            </div>

            {isHistoricalNumber && currentNumber ? (
              <p>
                {app.translator.trans('pakt21-beta.forum.id_page.historical_number')}{' '}
                <Link href={`/id/${currentNumber}`}>#{currentNumber}</Link>.
              </p>
            ) : (
              <p>{app.translator.trans('pakt21-beta.forum.id_page.current_number')}</p>
            )}

            <dl className="Pakt21IdPage-details">
              <div>
                <dt>{app.translator.trans('pakt21-beta.forum.id_page.trust_level')}</dt>
                <dd>{this.user.attribute('pakt21TrustLevel') || 0}</dd>
              </div>
              <div>
                <dt>{app.translator.trans('pakt21-beta.forum.id_page.verification')}</dt>
                <dd>{app.translator.trans(this.user.attribute('pakt21IsVerifiedAdult')
                  ? 'pakt21-beta.forum.user.status_verified'
                  : 'pakt21-beta.forum.user.status_unverified')}</dd>
              </div>
              {joined ? (
                <div>
                  <dt>{app.translator.trans('pakt21-beta.forum.id_page.joined')}</dt>
                  <dd>{new Intl.DateTimeFormat(document.documentElement.lang || 'pl', { dateStyle: 'long' }).format(joined)}</dd>
                </div>
              ) : null}
              {location ? (
                <div>
                  <dt>{app.translator.trans('pakt21-beta.forum.profile.location_heading')}</dt>
                  <dd>{location}</dd>
                </div>
              ) : null}
            </dl>

            {contribution ? (
              <section className="Pakt21IdPage-section">
                <h2>{app.translator.trans('pakt21-beta.forum.profile.contribution_heading')}</h2>
                <p>{contribution}</p>
              </section>
            ) : null}

            {needs ? (
              <section className="Pakt21IdPage-section">
                <h2>{app.translator.trans('pakt21-beta.forum.profile.needs_heading')}</h2>
                <p>{needs}</p>
              </section>
            ) : null}

            {skills.length ? (
              <section className="Pakt21IdPage-section">
                <h2>{app.translator.trans('pakt21-beta.forum.profile.skills_heading')}</h2>
                <div className="Pakt21SkillList">
                  {skills.map((skill) => <span className="Pakt21Skill" key={skill}>{skill}</span>)}
                </div>
              </section>
            ) : null}

            <section className="Pakt21IdPage-section Pakt21IdPage-history">
              <h2>{app.translator.trans('pakt21-beta.forum.id_page.history_heading')}</h2>
              <p>{app.translator.trans('pakt21-beta.forum.id_page.history_empty')}</p>
            </section>

            <div className="Pakt21IdPage-actions">
              {this.canEditUser() ? (
                <Button className="Button Button--primary" onclick={() => this.editUser()}>
                  {app.translator.trans('pakt21-beta.forum.id_page.edit_user')}
                </Button>
              ) : null}
              <Link className="Button" href={`/u/${this.user.slug()}`}>
                {app.translator.trans('pakt21-beta.forum.id_page.full_profile')}
              </Link>
            </div>
          </section>
        </div>
      </div>
    );
  }
}
